import { ajvResolver } from '@hookform/resolvers/ajv'
import {
  ArrowForward,
  ChevronLeft,
  CloudUploadOutlined,
  FolderOutlined,
  LockOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import esocial from 'assets/brazil-stamp.svg'
import logo from 'assets/logo-icon.png'
import ImageBanner from 'components/image-banner'
import SecuritySVG from 'components/svg/security'
import WrapperPermission from 'components/wrapper-permission'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useAuthentications } from 'contexts/authentications'
import { useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CertificateUploadSchema, { ICertificateUploadFormData } from 'schema/authentication/certificate-upload'
import api from 'services/api'
import { loadItem } from 'services/requests'
import OrganizationInfo from '../organization-info'
import { useStyles } from './styles'
import { uploadErrorMessage } from './upload-error-message'

interface IRouteParams {
  token: string
}

const CertificateUpload = () => {
  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState(false)
  const { refetchValidate } = useAuthentications()
  const { user, updateUserData, updateOnCertificateUpload } = useAuth()
  const { closeAlert, openAlert } = useAlert()

  const params = useParams<IRouteParams>()
  const history = useHistory()
  const classes = useStyles()
  const digitalCertificateRef = useRef<HTMLInputElement | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: ['users', 'find-by-refresh-token', params.token],
    queryFn: () => loadItem(`/users/find-by-refresh-token`, params.token),
    enabled: !!params.token,
  })

  const cnpj = data?.data?.data?.federalTaxId || ''
  const corporateName = data?.data?.data?.corporateName || ''

  const handleUploadFileClick = () => {
    digitalCertificateRef.current?.click()
  }

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ICertificateUploadFormData>({
    resolver: ajvResolver(CertificateUploadSchema),
  })

  const onSubmit = useCallback(
    async (formData: ICertificateUploadFormData) => {
      try {
        setIsLoading(true)
        const input = digitalCertificateRef.current

        if (!input?.files || input.files.length === 0) {
          openAlert('Selecione um arquivo de certificado!', 'error')
          setIsLoading(false)
          return
        }

        const userCnpj = user ? (user.organization?.cnpj as string) : cnpj

        const uploadData = new FormData()
        uploadData.append('cnpj', userCnpj)
        uploadData.append('certificate', input.files[0])
        uploadData.append('certificatePassword', formData.password)
        uploadData.append('acceptance', `${formData.acceptance}`)

        // Convidado sem conta: o backend so aceita a troca de certificado com o
        // token do e-mail de `/passwords/invite`, e tira o CNPJ dele. Logado, vale
        // a sessao (Authorization) e o CNPJ da organizacao ativa.
        await api.patch(
          '/users/certificate',
          uploadData,
          !user && params.token ? { headers: { 'x-responsible-invite-token': params.token } } : undefined
        )

        // await updateUserData()
        // await updateOnCertificateUpload()
        setIsLoading(false)
        // refetchValidate()
        history.push('/connected-certificate')
      } catch (err) {
        setIsLoading(false)
        const actualCnpj = user ? user.organization?.cnpj : cnpj

        if (!actualCnpj) {
          openAlert('Algo deu errado, Tente novamente mais tarde!', 'error')
          return
        }
        console.log(err)
        // 401 = link vencido (convidado) ou sessao expirada; 403 = outra empresa;
        // "senha invalida" so quando o backend diz que e a senha.
        openAlert(uploadErrorMessage(err, !user && !!params.token), 'error')
      }
    },
    [history, params, cnpj, user, updateUserData, openAlert, refetchValidate]
  )

  const handleChange = () => closeAlert()

  const handleBack = () => {
    history.push('/esocial-connect')
  }

  if (!params.token && !user) {
    history.push('/')
    return null
  }

  if (isFetching) {
    return <div>Loading...</div>
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.mainWrapper}>
        <WrapperPermission>
          <Box className={classes.connectBox}>
            <Box
              display='flex'
              alignItems='center'
            >
              <img
                src={logo}
                alt='Conducco'
                className={classes.logoImage}
              />
              <ArrowForward style={{ color: '#707974' }} />
              <img
                src={esocial}
                alt='E-Social'
                height={48}
                style={{ marginLeft: '12px' }}
              />
            </Box>

            <p className={classes.formTitle}>Conectar com eSocial</p>
            <p className={classes.formSubTitle}>
              Conecte seus dados do eSocial ao seu Cofre Digital da Conducco por meio de um{' '}
              <b>Certificado Digital A1</b>.
            </p>

            <OrganizationInfo
              organizationName={user?.organization?.name || corporateName}
              organizationFederalTaxId={user?.organization?.cnpj || cnpj}
            />

            <Box
              component='form'
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              data-testid='form'
              className={classes.formBox}
              marginTop='16px'
            >
              <TextField
                id='digitalCertificate'
                error={!!errors.digitalCertificate}
                helperText='Arquivos do tipo .P12 e .PFX'
                variant='outlined'
                margin='dense'
                size='small'
                label='Importar arquivo'
                fullWidth
                placeholder='Escolher arquivo'
                disabled
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  maxLength: 255,
                }}
                sx={{ mb: 4 }}
                InputProps={{
                  startAdornment: <FolderOutlined sx={{ marginRight: '8px' }} />,
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={handleUploadFileClick}
                      >
                        <CloudUploadOutlined />
                      </IconButton>
                      <input
                        type='file'
                        accept='.p12, .pfx'
                        hidden
                        ref={digitalCertificateRef}
                        onChange={(e) => setValue('digitalCertificate', e.target.files![0].name)}
                      />
                    </InputAdornment>
                  ),
                  sx: { paddingRight: '16px' },
                }}
                {...register('digitalCertificate', { onChange: handleChange })}
              />

              <TextField
                required
                id='password'
                type={isPasswordHidden ? 'password' : 'text'}
                autoFocus
                autoComplete='new-password'
                variant='outlined'
                error={Boolean(errors.password)}
                placeholder='Digite a Senha do Certificado'
                label='Senha do Certificado Digital A1'
                sx={{ mb: 2 }}
                {...register('password', { onChange: handleChange })}
                InputProps={{
                  startAdornment: (
                    <LockOutlined
                      color={isLoading ? 'disabled' : 'inherit'}
                      sx={{ marginRight: '12px' }}
                    />
                  ),
                  endAdornment: (
                    <IconButton
                      onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                      disabled={isLoading}
                    >
                      {isPasswordHidden ? (
                        <VisibilityOffOutlined color={isLoading ? 'disabled' : 'inherit'} />
                      ) : (
                        <VisibilityOutlined />
                      )}
                    </IconButton>
                  ),
                  sx: { paddingRight: '8px' },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setDisabled(!e.target.checked)}
                    disabled={isLoading}
                    sx={{
                      mt: '-50px',
                      borderColor: '#0f0f1e !important',
                      opacity: 0.6,
                    }}
                  />
                }
                label={
                  <div className={classes.acceptArea}>
                    <span>
                      Eu autorizo a Conducco a coletar dados em meu nome na base do eSocial. Li e concordo com os{' '}
                      <Link
                        download
                        href='/documents/Termose_Condicoes_Conducco-10_01_2024.pdf'
                      >
                        Termos de Uso
                      </Link>{' '}
                      e a{' '}
                      <Link
                        download
                        href='/documents/Politica_Privacidade.pdf'
                      >
                        Política de Privacidade
                      </Link>{' '}
                      da Conducco
                    </span>
                  </div>
                }
                {...register('acceptance', { onChange: handleChange })}
              />

              <Box
                display='flex'
                flexDirection='column'
                gap='1rem'
                alignItems='center'
                width='100%'
              >
                <Button
                  variant='contained'
                  type='submit'
                  size='large'
                  fullWidth
                  disabled={isLoading || disabled}
                  sx={{
                    mt: 2,
                    mb: 1,
                    '&.Mui-disabled': {
                      backgroundColor: '#8C9097 !important',
                      color: '#FFF !important',
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress
                      color='inherit'
                      size={26}
                    />
                  ) : (
                    'CONECTAR'
                  )}
                </Button>

                <Button
                  onClick={handleBack}
                  variant='text'
                  color='inherit'
                >
                  <ChevronLeft sx={{ mr: '8px', fontSize: '24px' }} />
                  Voltar
                </Button>
              </Box>
            </Box>
          </Box>
        </WrapperPermission>

        <ImageBanner
          title='A Conducco garante a privacidade e a segurança dos seus dados, seguindo rigorosos padrões de proteção.'
          banner={<SecuritySVG />}
        />
      </Box>
    </Box>
  )
}

export default CertificateUpload
